import React, { FC, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { usePermissions } from 'hooks/usePermissions'
import { useAppSelector } from 'redux/hooks'
import { checkUserSession } from 'redux/user/user-actions'
import { clearAccessTokens, getRefreshToken, setAccessTokens } from 'utils/accessHelpers'

import { COMPONENT_EXTENDER } from 'extender'

const ErrorBoundary = COMPONENT_EXTENDER('components/core/ErrorBoundary')
const ErrorPermissionPage = COMPONENT_EXTENDER('components/core/ErrorBoundary/ErrorPermission403')

export const AdminRoute = () => {
    const dispatch = useDispatch()
    const { accessToken } = useParams()

    clearAccessTokens()
    setAccessTokens(accessToken, null, null)

    useEffect(() => {
        dispatch(checkUserSession())
    }, [])

    return <Navigate replace to="/" />
}

interface IUserRouteProps {
    permission?: boolean
    notFound?: boolean
    routes?: any
    currentUser?: any
}

export const UserRoute: FC<IUserRouteProps> = ({ permission, notFound, routes }) => {
    const { currentUser } = useAppSelector(state => state.user)
    const refreshToken = getRefreshToken()
    const { pathname, state } = useLocation()
    const { getPermission } = usePermissions()

    const matchHomePath = pathname === '/'
    const matchVerificationPath = pathname === '/verification'

    const matchWizardPath =
        pathname === '/welcome' ||
        pathname === '/phone-verification' ||
        pathname === '/two-fa-setup' ||
        pathname === '/identity' ||
        pathname === '/questionnaire' ||
        pathname === '/documents' ||
        pathname === '/payment' ||
        pathname === '/thank-you'

    const isKysStatusVerified = currentUser?.kycStatus === 'Verified'
    const isKycSubStatusNone = currentUser?.kycSubStatus === 'None'

    const matchRoute = routes?.find(o => o.path === pathname) || true

    if (!currentUser && !refreshToken) {
        return matchHomePath ? (
            <Navigate replace to="/sign-in" />
        ) : (
            <Navigate replace to="/sign-in" state={{ ...state, redirectAfterLogin: pathname }} />
        )
    }

    if (!!currentUser && !currentUser?.isEmailConfirmed) {
        return <Navigate replace to="/success" />
    }

    if (
        (getPermission('Onboarding') && !!matchVerificationPath) ||
        (!getPermission('Onboarding') && !!matchVerificationPath && isKysStatusVerified && isKycSubStatusNone) ||
        (!getPermission('Onboarding') && !getPermission('EnableWizard') && !!matchWizardPath)
    ) {
        return <Navigate replace to="/" />
    }

    if (!!currentUser && ((permission !== undefined && !permission) || (matchRoute?.permission !== undefined && !matchRoute?.permission))) {
        return <ErrorPermissionPage />
    }

    if (!!currentUser && (notFound || !matchRoute)) {
        return <Navigate replace to="/not-found" />
    }

    return (
        <ErrorBoundary>
            <Outlet />
        </ErrorBoundary>
    )
}

interface ILandingRouteProps {
    permission?: boolean
}

export const LandingRoute: FC<ILandingRouteProps> = ({ permission }) => {
    const { currentUser } = useAppSelector(state => state.user)
    const { state } = useLocation()
    const { redirectAfterLogin = null } = { ...state }

    if (currentUser) {
        window.location.href = redirectAfterLogin ? redirectAfterLogin : '/'
    }
    if (permission !== undefined && !permission) {
        return <Navigate replace to="/not-found" />
    }

    return (
        <ErrorBoundary>
            <Outlet />
        </ErrorBoundary>
    )
}
